import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { AuthBoundary } from "../shared/auth/AuthBoundary";
import type { Profile } from "../shared/types/database";
import {
  searchAuthoring,
  type SearchResult,
} from "./authoring/authoringRepository";
import { GlobalActivityPanel } from "./authoring/GlobalActivityPanel";
import { authoringSaveCoordinator, SAVE_STATE_LABELS } from "./authoring/saveCoordinator";
import { EditorProfileProvider } from "./authoring/EditorProfileContext";
import { RuntimePreviewFrame } from "./runtime-preview/RuntimePreviewFrame";
import { authoringErrorMessage } from "./authoring/errorMessage";
import {
  assembleDraftPreview,
  EDITOR_PREVIEW_TARGETS,
  invalidateDraftPreview,
  previewScreenForRoute,
} from "./runtime-preview/DraftPreviewAssembler";
import type { ContentSnapshotV2 } from "../runtime/content/ContentSnapshotV2";
import {
  ADVANCED_EDITOR_NAV,
  resolveEditorMode,
  routeForMode,
  SIMPLE_EDITOR_NAV,
  withEditorMode,
  type EditorMode,
  type EditorNavGroup,
  type EditorNavItem,
} from "./navigation/editorModes";
import { JourneyWorkflowBar } from "./journeys/JourneyWorkflowBar";

const AssetModuleRoute = lazy(() => import("./assets/AssetModuleRoute"));
const CoreFoundationModuleRoute = lazy(() => import("./core/CoreFoundationModuleRoute"));
const TestPlayerModule = lazy(() => import("./test-player/TestPlayerModule"));
const ScreenEditorModuleRoute = lazy(
  () => import("./screens/ScreenEditorModuleRoute"),
);
const BondModuleRoute = lazy(() => import("./bonds/BondModuleRoute"));
const CosmeticModuleRoute = lazy(
  () => import("./cosmetics/CosmeticModuleRoute"),
);
const EnemyModuleRoute = lazy(() => import("./enemies/EnemyModuleRoute"));
const CombatModuleRoute = lazy(() => import("./combat/CombatModuleRoute"));
const CombatProfileModuleRoute = lazy(
  () => import("./combat/CombatProfileModuleRoute"),
);
const RewardModuleRoute = lazy(() => import("./rewards/RewardModuleRoute"));
const MissionModuleRoute = lazy(() => import("./missions/MissionModuleRoute"));
const BlackKeepModuleRoute = lazy(
  () => import("./black-keep/BlackKeepModuleRoute"),
);
const WorldHuntModuleRoute = lazy(
  () => import("./world-hunts/WorldHuntModuleRoute"),
);
const KingdomThreatModuleRoute = lazy(
  () => import("./kingdom-threats/KingdomThreatModuleRoute"),
);
const ExpeditionModuleRoute = lazy(
  () => import("./expeditions/ExpeditionModuleRoute"),
);
const DialogueModuleRoute = lazy(() => import("./dialogue/DialogueModuleRoute"));
const PvpModuleRoute = lazy(() => import("./pvp/PvpModuleRoute"));
const NeighbourhoodTemplateModuleRoute = lazy(
  () => import("./neighbourhood/NeighbourhoodTemplateModuleRoute"),
);
const WorldSimpleModuleRoute = lazy(
  () => import("./neighbourhood/WorldSimpleModuleRoute"),
);
const ValidationModuleRoute = lazy(() => import("./production/ValidationModuleRoute"));
const PublishingModuleRoute = lazy(() => import("./production/PublishingModuleRoute"));
const RuntimeDebugModuleRoute = lazy(() => import("./runtime-debug/RuntimeDebugModuleRoute"));
const ProjectDashboard = lazy(() => import("./journeys/ProjectDashboard"));

function TopSearch({ mode }: { mode: EditorMode }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (path: string) => navigate(withEditorMode(path, mode));
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(
      () =>
        void searchAuthoring(query).then((rows) => {
          if (!cancelled) setResults(rows);
        }),
      250,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);
  return (
    <div className="top-search">
      <input
        aria-label="Search all authoring content"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search content records, IDs, assets…"
      />
      {open && results.length > 0 && (
        <div className="top-search-results">
          {results.map((result) => (
            <button
              key={`${result.entity_type}:${result.entity_id}`}
              onClick={() => {
                if (result.entity_type === "asset")
                  go(`/assets/${result.entity_id}`);
                if (result.entity_type === "core_knight_definition")
                  go("/core");
                if (result.entity_type === "ui_document") go("/screens");
                if (["core_building_definition", "weapon_definition", "trinket_definition", "troop_definition"].includes(result.entity_type))
                  go("/core");
                if (result.entity_type === "bond_interaction")
                  go(`/bonds/${result.entity_id}`);
                if (result.entity_type === "princess_cosmetic")
                  go(`/cosmetics/${result.entity_id}`);
                if (result.entity_type === "enemy_definition")
                  go(`/enemies/${result.entity_id}`);
                if (result.entity_type === "modifier_definition")
                  go(`/modifiers/${result.entity_id}`);
                if (result.entity_type === "combat_resolution_profile")
                  go(`/combat-profiles/${result.entity_id}`);
                if (result.entity_type === "reward_table")
                  go(`/rewards/${result.entity_id}`);
                if (result.entity_type === "core_resource_definition")
                  go("/core");
                if (result.entity_type === "mission_definition")
                  go(`/missions/${result.entity_id}`);
                if (result.entity_type === "black_keep_floor_definition")
                  go("/black-keep");
                if (
                  result.entity_type === "world_hunt_definition" ||
                  result.entity_type === "world_hunt_spawn_slot"
                )
                  go("/world-hunts");
                if (result.entity_type === "kingdom_threat_definition")
                  go("/kingdom-threats");
                if (result.entity_type === "expedition")
                  go("/expeditions");
                if (["neighbourhood_template", "neighbourhood_sector", "neighbourhood_location", "neighbourhood_route"].includes(result.entity_type))
                  go("/neighbourhood");
                setOpen(false);
              }}
            >
              <strong>{result.entity_name}</strong>
              <span>
                {result.entity_type} · {result.entity_key}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function targetMatches(item: EditorNavItem, pathname: string, search: string) {
  const target = new URL(item.to, "https://editor.local");
  const pathMatches = target.pathname === "/" ? pathname === "/" : pathname === target.pathname || pathname.startsWith(`${target.pathname}/`);
  if (!pathMatches) return false;
  const current = new URLSearchParams(search);
  for (const [key, value] of target.searchParams) if (current.get(key) !== value) return false;
  return true;
}

function currentSimpleWorkspace(pathname: string, search: string): EditorNavGroup {
  const params = new URLSearchParams(search);
  const requested = params.get("workspace");
  if (requested) {
    const explicit = SIMPLE_EDITOR_NAV.find((group) => group.id === requested);
    if (explicit) return explicit;
  }
  if (pathname === "/core") return SIMPLE_EDITOR_NAV.find((group) => group.id === (["knights", "weapons", "trinkets"].includes(params.get("section") ?? "") ? "knights" : "kingdom"))!;
  if (["/missions", "/black-keep", "/expeditions", "/world-hunts", "/kingdom-threats"].some((path) => pathname.startsWith(path))) return SIMPLE_EDITOR_NAV.find((group) => group.id === "pve")!;
  if (pathname.startsWith("/neighbourhood") || pathname.startsWith("/pvp")) return SIMPLE_EDITOR_NAV.find((group) => group.id === "world")!;
  if (["/screens", "/dialogue", "/cosmetics", "/assets"].some((path) => pathname.startsWith(path))) return SIMPLE_EDITOR_NAV.find((group) => group.id === "visual")!;
  if (pathname.startsWith("/test-player") || pathname.startsWith("/validation")) return SIMPLE_EDITOR_NAV.find((group) => group.id === "tools")!;
  return SIMPLE_EDITOR_NAV[0]!;
}

const PVE_ACTIVITIES = [
  { label: "Mission Board", description: "Timed Knight missions", to: "/missions", icon: "▤" },
  { label: "Black Keep", description: "Permanent floor challenge", to: "/black-keep", icon: "▥" },
  { label: "Expeditions", description: "Branching multi-step runs", to: "/expeditions?workspace=pve", icon: "⌁" },
  { label: "World Hunts", description: "Map-based PvE marches", to: "/world-hunts", icon: "◎" },
  { label: "Kingdom Threat", description: "Shared neighbourhood boss", to: "/kingdom-threats", icon: "♛" },
] as const;

function PveActivityGrid({ pathname }: { pathname: string }) {
  return <nav className="pve-activity-grid" aria-label="PvE activities">
    {PVE_ACTIVITIES.map((activity) => <Link key={activity.to} className={pathname.startsWith(activity.to.split("?")[0]!) ? "active" : ""} to={withEditorMode(activity.to, "simple")}><span>{activity.icon}</span><strong>{activity.label}</strong><small>{activity.description}</small></Link>)}
  </nav>;
}

function LivePreviewDock({ pathname, refreshToken, drawerOpen, onClose }: { pathname: string; refreshToken: string; drawerOpen: boolean; onClose: () => void }) {
  const [selection, setSelection] = useState("follow");
  const [followScreen, setFollowScreen] = useState<string>();
  const [scale, setScale] = useState(0.3);
  const [snapshot, setSnapshot] = useState<ContentSnapshotV2>();
  const [error, setError] = useState<string>();
  const [runtimeReady, setRuntimeReady] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const resize = () => setScale(Math.max(0.05, Math.min(frame.clientWidth / 1080, frame.clientHeight / 2520)));
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    setFollowScreen(undefined);
    const receive = (event: Event) => {
      const detail = (event as CustomEvent<{ screenKey?: string }>).detail;
      if (detail?.screenKey) setFollowScreen(detail.screenKey);
    };
    window.addEventListener("editor-preview-screen", receive);
    return () => window.removeEventListener("editor-preview-screen", receive);
  }, [pathname]);
  const screen = selection === "follow" ? followScreen ?? previewScreenForRoute(pathname) : EDITOR_PREVIEW_TARGETS.find((target) => target.id === selection)?.screenKey ?? "screen_home";
  useEffect(() => {
    let cancelled = false;
    setError(undefined);
    if (refreshToken === "saved") invalidateDraftPreview(screen);
    void assembleDraftPreview(screen).then((next) => {
      if (!cancelled) setSnapshot(next);
    }).catch((reason) => {
      if (!cancelled) { setRuntimeReady(false); setSnapshot(undefined); setError(authoringErrorMessage(reason)); }
    });
    return () => { cancelled = true; };
  }, [refreshToken, screen]);
  return <aside className={`editor-live-preview${drawerOpen ? " preview-drawer-open" : ""}`} aria-label="Live mobile preview">
    <header><strong>LIVE MOBILE PREVIEW · 1080×2340</strong><select aria-label="Preview screen" value={selection} onChange={(event) => setSelection(event.target.value)}><option value="follow">Follow editor</option>{EDITOR_PREVIEW_TARGETS.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}</select><button type="button" title="Fit preview" onClick={() => frameRef.current && setScale(Math.min(frameRef.current.clientWidth / 1080, frameRef.current.clientHeight / 2520))}>▣</button><button className="preview-drawer-close" type="button" aria-label="Close preview" onClick={onClose}>×</button></header>
    <div className="editor-phone-frame" ref={frameRef} data-preview-state={error ? "error" : runtimeReady ? "ready" : "loading"}>
      <div className="editor-phone-scale" style={{ width: 1080, height: 2520, left: "50%", transform: `translateX(-50%) scale(${scale})`, transformOrigin: "top center" }}>
        {snapshot ? <RuntimePreviewFrame snapshot={snapshot} screenKey={screen} editMode={false} onReady={() => setRuntimeReady(true)} /> : <div className={error ? "preview-error-state" : "preview-loading-state"}>{error ?? "Loading draft preview…"}</div>}
      </div>
    </div>
  </aside>;
}

function EditorChrome({ profile }: { profile: Profile }) {
  const [activityOpen, setActivityOpen] = useState(false);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storedMode = typeof window === "undefined" ? null : window.localStorage.getItem("mkibty.editor.mode");
  const mode = resolveEditorMode(`?${searchParams.toString()}`, storedMode);
  const simpleWorkspace = currentSimpleWorkspace(location.pathname, location.search);
  const activeAdvancedItem = ADVANCED_EDITOR_NAV.flatMap((group) => group.items.map((item) => ({ group, item }))).find(({ item }) => targetMatches(item, location.pathname, location.search));
  const visualWorkspace = searchParams.get("workspace") === "visual";
  const modalWorkspace = location.pathname.startsWith("/screens") && searchParams.get("kind") === "template";
  const visualRoute = !modalWorkspace && (location.pathname.startsWith("/screens") || visualWorkspace);
  useEffect(() => {
    window.localStorage.setItem("mkibty.editor.mode", mode);
  }, [mode]);

  const changeMode = (nextMode: EditorMode) => {
    navigate(withEditorMode(routeForMode(location.pathname, nextMode), nextMode), { replace: true });
  };
  const saveState = useSyncExternalStore(
    authoringSaveCoordinator.subscribe,
    authoringSaveCoordinator.getSnapshot,
    authoringSaveCoordinator.getSnapshot,
  );
  const commandState = useSyncExternalStore(
    authoringSaveCoordinator.subscribe,
    authoringSaveCoordinator.getCommandSnapshot,
    authoringSaveCoordinator.getCommandSnapshot,
  );
  const canUndo = commandState.startsWith("undo");
  const canRedo = commandState.endsWith("redo");
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, [contenteditable='true']")) return;
      if (event.key.toLowerCase() === "z" && !event.shiftKey) { event.preventDefault(); void authoringSaveCoordinator.undoLast(); }
      if (event.key.toLowerCase() === "y" || (event.key.toLowerCase() === "z" && event.shiftKey)) { event.preventDefault(); void authoringSaveCoordinator.redoLast(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  return (
      <div className="editor-foundation" data-testid="editor-shell" data-editor-mode={mode} data-preview-hidden={visualRoute || undefined}>
        <header className="editor-header">
          <Link className="editor-brand" to={withEditorMode("/", mode)}>
            <span className="editor-brand-crest">♜</span>
            <span><strong>My Knights Are Better</strong><small>Game Editor</small></span>
          </Link>
          <TopSearch mode={mode} />
          <div className="editor-header-actions">
            <div className="editor-mode-switch" role="group" aria-label="Editor mode">
              <button type="button" aria-pressed={mode === "simple"} onClick={() => changeMode("simple")}>Simple</button>
              <button type="button" aria-pressed={mode === "advanced"} onClick={() => changeMode("advanced")}>Advanced</button>
            </div>
            <span className="editor-current-badge">CURRENT</span>
            {!visualRoute && <button className="preview-drawer-toggle" type="button" onClick={() => setPreviewDrawerOpen(true)}>Preview</button>}
            <button className="editor-icon-action editor-undo" type="button" title="Undo last authoring change (Ctrl+Z)" disabled={!canUndo} onClick={() => void authoringSaveCoordinator.undoLast()}>Undo</button>
            <button className="editor-icon-action editor-redo" type="button" title="Redo last authoring change (Ctrl+Y)" disabled={!canRedo} onClick={() => void authoringSaveCoordinator.redoLast()}>Redo</button>
            {mode === "advanced" && <button className="editor-icon-action" title="History" onClick={() => setActivityOpen(true)}>History</button>}
            <span className="save-indicator" data-save-state={saveState}>Save Draft · {SAVE_STATE_LABELS[saveState]}</span>
            {mode === "advanced" && <div className="editor-identity">
              <span
                style={{ background: profile.editor_colour ?? "#8ea6d8" }}
              />
              {profile.display_name}
            </div>}
          </div>
        </header>
        <aside className="editor-nav" aria-label="Authoring sections">
          {mode === "simple" ? <>
            <strong>GAME EDITOR</strong>
            {SIMPLE_EDITOR_NAV.map((group) => <Link key={group.id} className={`workspace-link${simpleWorkspace.id === group.id ? " active" : ""}`} to={withEditorMode(group.items[0]!.to, mode)}><span className="workspace-icon">{group.icon}</span>{group.label}</Link>)}
            <div className="mode-sidebar-note"><b>Simple mode</b><br/>Only the seven everyday workspaces are shown. Balance formulas, runtime systems, schedules, localization, history and technical settings are hidden.</div>
          </> : <>
            <div className="mode-sidebar-note advanced"><b>Advanced mode</b><br/>Direct access to balance, runtime and technical authoring.</div>
            {ADVANCED_EDITOR_NAV.map((group) => <div className="nav-group" key={group.label}><strong>{group.label}</strong>{group.items.map((item) => <Link key={`${group.label}:${item.label}`} to={withEditorMode(item.to, mode)} className={targetMatches(item, location.pathname, location.search) ? "active" : ""}><span className="workspace-icon">{item.icon}</span><span>{item.label}</span>{item.badge && <small className={`nav-badge ${item.badge.toLowerCase()}`}>{item.badge}</small>}</Link>)}</div>)}
          </>}
        </aside>
        <main className="editor-main">
          <header className="editor-workspace-header">
            <div className="editor-breadcrumb">{mode === "simple" ? simpleWorkspace.label : activeAdvancedItem?.group.label ?? "GAME"} / {mode === "simple" ? simpleWorkspace.items.find((item) => targetMatches(item, location.pathname, location.search))?.label ?? simpleWorkspace.items[0]?.label : activeAdvancedItem?.item.label ?? "Overview"}</div>
            <div className="editor-workspace-title"><div><div className="workspace-heading-line"><h1>{mode === "simple" ? simpleWorkspace.label : activeAdvancedItem?.item.label ?? "Overview"}</h1><span className="workspace-draft-chip">Draft</span></div>{mode === "simple" && <p>{simpleWorkspace.description}</p>}</div><Link className="workspace-playtest" to={withEditorMode("/test-player", mode)}>Playtest</Link></div>
            {mode === "simple" && <nav className="editor-workspace-tabs">{(simpleWorkspace.id === "pve" ? [{ label: "PvE Activities", to: location.pathname }] : simpleWorkspace.items).map((item) => <Link key={item.label} className={simpleWorkspace.id === "pve" || targetMatches(item, location.pathname, location.search) ? "active" : ""} to={withEditorMode(item.to, mode)}>{item.label}</Link>)}</nav>}
          </header>
          <EditorProfileProvider profile={profile}>
            {mode === "simple" && simpleWorkspace.id === "pve" && <PveActivityGrid pathname={location.pathname} />}
            {mode === "simple" && <JourneyWorkflowBar pathname={location.pathname} search={location.search} />}
            <Suspense fallback={<p>Loading module…</p>}>
              <Routes>
                <Route index element={<ProjectDashboard />} />
                <Route path="screens" element={<ScreenEditorModuleRoute />} />
                <Route path="dialogue" element={<DialogueModuleRoute />} />
                <Route path="core" element={<CoreFoundationModuleRoute />} />
                <Route path="assets" element={<AssetModuleRoute />} />
                <Route path="assets/:assetId" element={<AssetModuleRoute />} />
                <Route path="bonds" element={<BondModuleRoute />} />
                <Route path="bonds/:bondId" element={<BondModuleRoute />} />
                <Route path="cosmetics" element={<CosmeticModuleRoute />} />
                <Route
                  path="cosmetics/:cosmeticId"
                  element={<CosmeticModuleRoute />}
                />
                <Route path="missions" element={<MissionModuleRoute simple={mode === "simple"} />} />
                <Route
                  path="missions/:missionId"
                  element={<MissionModuleRoute simple={mode === "simple"} />}
                />
                <Route path="black-keep" element={<BlackKeepModuleRoute />} />
                <Route path="world-hunts" element={<WorldHuntModuleRoute />} />
                <Route
                  path="kingdom-threats"
                  element={<KingdomThreatModuleRoute />}
                />
                <Route path="expeditions" element={<ExpeditionModuleRoute />} />
                <Route path="neighbourhood" element={mode === "simple" && !visualWorkspace ? <WorldSimpleModuleRoute /> : <NeighbourhoodTemplateModuleRoute />} />
                <Route path="pvp/:section" element={<PvpModuleRoute />} />
                <Route path="validation" element={<ValidationModuleRoute />} />
                <Route path="publishing" element={<PublishingModuleRoute />} />
                <Route path="enemies" element={<EnemyModuleRoute />} />
                <Route path="enemies/:enemyId" element={<EnemyModuleRoute />} />
                <Route path="modifiers" element={<CombatModuleRoute />} />
                <Route
                  path="modifiers/:modifierId"
                  element={<CombatModuleRoute />}
                />
                <Route
                  path="combat-profiles"
                  element={<CombatProfileModuleRoute />}
                />
                <Route
                  path="combat-profiles/:profileId"
                  element={<CombatProfileModuleRoute />}
                />
                <Route path="rewards" element={<RewardModuleRoute />} />
                <Route
                  path="rewards/:rewardId"
                  element={<RewardModuleRoute />}
                />
                <Route path="test-player" element={<TestPlayerModule />} />
                <Route path="runtime-debug" element={<RuntimeDebugModuleRoute />} />
                <Route path="*" element={<Navigate to={withEditorMode("/", mode)} replace />} />
              </Routes>
            </Suspense>
          </EditorProfileProvider>
        </main>
          {!visualRoute && <LivePreviewDock pathname={`${location.pathname}${location.search}`} refreshToken={saveState} drawerOpen={previewDrawerOpen} onClose={() => setPreviewDrawerOpen(false)} />}
        {activityOpen && (
          <GlobalActivityPanel onClose={() => setActivityOpen(false)} />
        )}
      </div>
  );
}

function EditorShell({ profile }: { profile: Profile }) {
  return <BrowserRouter><EditorChrome profile={profile} /></BrowserRouter>;
}

export function EditorApp() {
  return (
    <AuthBoundary requirement="admin">
      {(profile) => <EditorShell profile={profile} />}
    </AuthBoundary>
  );
}
